import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, AreaChart, Area } from "recharts";
import { Waves } from "lucide-react";

export default function Rhythm() {
  const t = useT();
  const q = useQuery({
    queryKey: ["rhythm"],
    queryFn: async () => (await api.get("/rhythm")).data,
    retry: false,
  });

  const dataRaw = q.data?.days || q.data?.trend || q.data?.points || (Array.isArray(q.data) ? q.data : []);
  const data = Array.isArray(dataRaw)
    ? dataRaw.map((p, i) => ({
        day: p.day || p.date || `D${i + 1}`,
        energy: Number(p.energy ?? p.value ?? 0),
        mood: Number(p.mood ?? p.score ?? 0),
      }))
    : [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.rhythm")} title={t("nav.rhythm")} subtitle={t("brand.tagline")} />
      {q.isLoading && <LoadingCard height="h-64" />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && data.length === 0 && (
        <EmptyState icon={Waves} title={t("common.empty")} description="Your rhythm builds as you check in." testId="rhythm-empty" />
      )}
      {data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="rhythm-energy">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">Energy</div>
            <div className="font-heading text-xl text-ink-900">Energy over time</div>
            <div className="h-56 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="e-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#728468" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#728468" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                  <XAxis dataKey="day" fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={24} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                  <Area type="monotone" dataKey="energy" stroke="#728468" strokeWidth={2} fill="url(#e-grad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-3xl bg-white shadow-card p-6" data-testid="rhythm-mood">
            <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-500">Mood</div>
            <div className="font-heading text-xl text-ink-900">Mood over time</div>
            <div className="h-56 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data}>
                  <CartesianGrid stroke="#E8E4DD" strokeDasharray="3 3" />
                  <XAxis dataKey="day" fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} stroke="#8A847C" tickLine={false} axisLine={false} width={24} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E4DD" }} />
                  <Line type="monotone" dataKey="mood" stroke="#D27D60" strokeWidth={2} dot={{ r: 3, fill: "#D27D60" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
